import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import PageHeaderButton from "../components/page-header-button";
import {Button} from "primereact/button";
import {useTranslate} from "../../system/useTranslate";
import Icon from "../../system/components/icon";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import Table from "../../system/components/table";
import {useProject} from "../../models/project/use-project";
import {BreadCrumb} from "primereact/breadcrumb";
import CreateProjectDialog from "./components/create-project-dialog";
import {
    setWorkspaceGraphProjects,
    upsertWorkspaceProject,
    workspaceGraphSelectors,
} from "../../system/workspace-graph-slice";
import {filterMyProjectsScope} from "../../models/project/my-projects-scope";
import {useWorkspaceRole} from "../../system/use-workspace-role";
import {usePermission} from "../../system/usePermission";
import {usePublishAiScope} from "../../system/ai-scope-context";
import AiProjectCreationButton from "../../system/components/ai-project-creation-button";
import {normalizeApiError} from "../../system/api";
import {projectAccessRolesKey} from "../../models/project/project-access";

function Index(props) {
    const {config, workspaceMembershipData, workspaceId} = useSelector((state) => state.app);
    const workspaceProjects = useSelector(workspaceGraphSelectors.projects);
    const translate = useTranslate();
    const project = useProject();
    const navigate = useNavigate();
    const [breadCrumb] = useState([{label:translate("projects")},{}]);
    const [visible, setVisible] = useState(false);
    const [projects, setProjects] = useState([])
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState("");
    const dispatch = useDispatch();
    const [searchText, setSearchText] = useState("");
    const {userRoles} = useWorkspaceRole();
    const userRolesKey = useMemo(() => projectAccessRolesKey(userRoles), [userRoles]);
    const permission = usePermission();
    const canCreateProject = permission.can("create-new-projects");
    const canCreateGoal = permission.can("create-new-goals");
    const visibleProjects = useMemo(
        () => projects?.filter((item) => item.title?.includes(searchText)) || [],
        [projects, searchText],
    );

    usePublishAiScope({
        activeTab: "projects-list",
        selectedEntityIds: visibleProjects.map((item) => item.documentId || item.id),
        filters: {search: searchText, scopeSelectionProvided: true},
    });

    useEffect(()=>{
        // console.log(workspaceId)
        if (workspaceId){
            getProjects();
        }
        // Model methods have unstable identities. This content key refreshes
        // only after an effective role change, not after an equivalent Redux update.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        workspaceId,
        workspaceMembershipData?.workspace.id,
        workspaceMembershipData?.documentId,
        userRolesKey,
    ])

    useEffect(() => {
        setProjects(filterMyProjectsScope(
            workspaceProjects,
            workspaceMembershipData?.workspace,
            workspaceMembershipData,
            userRoles,
        ));
    }, [
        workspaceProjects,
        workspaceMembershipData?.id,
        workspaceMembershipData?.documentId,
        workspaceMembershipData?.workspace?.id,
        workspaceMembershipData?.workspace?.documentId,
        userRoles,
    ]);

    const getProjects = async ()=>{
        let _data = await project.getProjects(workspaceMembershipData?.workspace.id);

        setProjects(_data);
        dispatch(setWorkspaceGraphProjects(_data));
    }
    const createProject = async (projectData)=>{
        if (!permission.can("create-new-projects", true)) return;
        setCreating(true);
        setCreateError("");
        try {
            const createdProject = await project.createProject({
                ...projectData,
                workspace: workspaceMembershipData?.workspace.id,
            }, {throwOnError: true});
            if (!createdProject) throw new Error("project.error.create-failed");

            // Project membership grants the selected people viewer access. An
            // explicit role row is only needed after a non-default role is set.
            dispatch(upsertWorkspaceProject(createdProject));
            setVisible(false);
            return createdProject;
        } catch (requestError) {
            const errorKey = normalizeApiError(requestError)?.message || "project.error.create-failed";
            setCreateError(translate(errorKey));
            return null;
        } finally {
            setCreating(false);
        }
    }

    return (
        <div className="h-full flex flex-column overflow-hidden pb-3">
            <BreadCrumb
                separatorIcon={<Icon icon="mi mi:chevron_left text-xs"/>}
                className="bg-transparent border-none text-xs"
                model={breadCrumb}
            />
            <PageHeaderButton
                end={(
                    (canCreateProject || canCreateGoal) && (
                        <div className="flex flex-wrap align-items-center gap-2">
                            <AiProjectCreationButton
                                initialType="project"
                                context={{
                                    pageType: "projects",
                                    route: "/projects",
                                    contextVersion: 1,
                                    selectedEntityIds: visibleProjects.map((item) => item.documentId || item.id),
                                    filters: {search: searchText, scopeSelectionProvided: true},
                                }}
                                onCompleted={getProjects}
                            />
                            {canCreateProject && (
                                <Button
                                    onClick={() => {
                                        setCreateError("");
                                        setVisible(true);
                                    }}
                                    icon={<Icon icon="mi mi:add" className="text-lg ml-1"/>}
                                    className="text-xs gap-2"
                                    label={translate("create-project")}
                                />
                            )}
                        </div>
                    )
                )}
                title={props.name || ""}
                description={props.description}
            />
            <div className="flex flex-column gap-3 p-3 flex-1 overflow-auto">
                <div className="flex align-items-center gap-2">
                    <IconField className="align-items-center flex w-8 lg:w-4">
                        <InputIcon className="pi pi-search"/>
                        <InputText value={searchText} onChange={(e)=>{setSearchText(e.target.value)}} className="w-full border-round-lg pt-2 pb-2 text-sm" placeholder={translate("search-projects-placeholder")} />
                    </IconField>
                </div>
                <div className="flex-1 border-1 border-100 border-round-3xl shadow-1 pt-3 pb-3">
                    <Table showFooter={false} onRowClick={(item)=>{navigate(`/projects/${item.value.documentId}`)}} fields={project.prepareTable({config, actions:{}}).fields} fieldsConfig={project.prepareTable({config, actions:{}}).fieldsConfig} showHeader={false} value={visibleProjects} style={null}/>
                </div>
            </div>
            <CreateProjectDialog
                visible={visible}
                setVisible={setVisible}
                submitting={creating}
                error={createError}
                onSubmit={(e)=>{createProject(e.value, e.payload)}}
            />
        </div>
    );
}

export default Index;
